import { useMemo, useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Target, GitBranch } from 'lucide-react'
import './OpportunitiesList.css'
import {
  PageHeader, SectionCard, Badge, EmptyState, KpiTile, KpiGrid,
  DataTable, ListToolbar, useViewMode, type Column,
} from '@/components/ui'
import type { BadgeTone } from '@/components/ui'
import { useStore } from '@/store/useStore'
import { accountById, contactById, contactName } from '@/lib/selectors'
import { money, date } from '@/lib/format'
import type {
  Dataset, Opportunity, OpportunityType, CompetitivePosition,
} from '@/types/entities'

/* ---- type & stage presentation ---- */
const TYPE_TONE: Record<OpportunityType, BadgeTone> = {
  JV: 'navy',
  'Equity Partner': 'info',
  '3PM': 'ok',
  Acquisition: 'warn',
  Development: 'warn',
}

const POSITION_TONE: Record<CompetitivePosition, BadgeTone> = {
  'Partner of Choice': 'ok',
  'Competitive Plus': 'ok',
  Competitive: 'neutral',
  'Competitive Minus': 'warn',
}

/* Pipeline columns, ordered left→right from first touch to won. */
const STAGE_ORDER = [
  'Initial Outreach',
  'Qualifying',
  'Proposal',
  'Negotiation',
  'Closed Won',
]
const STAGE_DOT: Record<string, string> = {
  'Initial Outreach': 'var(--wpi-platinum)',
  Qualifying: 'var(--wpi-blue-60)',
  Proposal: 'var(--wpi-logo-blue)',
  Negotiation: 'var(--wpi-cyan)',
  'Closed Won': 'var(--ok)',
}

function stageBucket(o: Opportunity): string {
  if (STAGE_ORDER.includes(o.salesStage)) return o.salesStage
  if (/won/i.test(o.salesStage)) return 'Closed Won'
  return 'Initial Outreach'
}

export default function OpportunitiesList() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [view, setView] = useState('all')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useViewMode('opportunities', 'cards')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return data.opportunities.filter((o) => {
      if (view === 'equity' && o.type !== 'Equity Partner') return false
      if (view === '3pm' && o.type !== '3PM') return false
      if (view === 'jv' && o.type !== 'JV') return false
      if (view === 'open' && /won|lost/i.test(o.salesStage)) return false
      if (!q) return true
      const acct = accountById(data, o.principalAccountId)
      return (
        o.name.toLowerCase().includes(q) ||
        (acct?.name.toLowerCase().includes(q) ?? false) ||
        o.type.toLowerCase().includes(q)
      )
    })
  }, [data, view, search])

  /* ---- pipeline KPIs ---- */
  const openOpps = filtered.filter((o) => !/won|lost/i.test(o.salesStage))
  const wonOpps = filtered.filter((o) => /won/i.test(o.salesStage))
  const openValue = openOpps.reduce((s, o) => s + (o.dealSize ?? 0), 0)
  const wonValue = wonOpps.reduce((s, o) => s + (o.dealSize ?? 0), 0)

  /* ---- group into columns ---- */
  const columns = STAGE_ORDER.map((stage) => {
    const items = filtered.filter((o) => stageBucket(o) === stage)
    const sum = items.reduce((s, o) => s + (o.dealSize ?? 0), 0)
    return { stage, items, sum }
  })

  const views = [
    { id: 'all', label: 'All Deals' },
    { id: 'open', label: 'Open' },
    { id: 'equity', label: 'Equity Partner' },
    { id: '3pm', label: '3PM' },
    { id: 'jv', label: 'JV' },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Pipeline"
        title={<>Deal <b>Pipeline</b></>}
        sub="Capital-raise, JV, and third-party management opportunities across all relationship tracks."
      />

      <KpiGrid cols={4}>
        <KpiTile label="Open Deals" value={openOpps.length} sub="in active pursuit" />
        <KpiTile label="Open Pipeline" value={money(openValue, { compact: true })} accent sub="prospective commitment" />
        <KpiTile label="Closed Won" value={wonOpps.length} sub="owned + managed" />
        <KpiTile label="Won Value" value={money(wonValue, { compact: true })} sub="committed capital + JV" />
      </KpiGrid>

      <ListToolbar
        views={views}
        activeView={view}
        onView={setView}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search deals or accounts…"
        count={filtered.length}
        viewMode={viewMode}
        onViewMode={setViewMode}
        addLabel="New Deal"
        onAdd={() => {}}
        onExport={() => {}}
      />

      {filtered.length === 0 ? (
        <SectionCard pad>
          <EmptyState
            icon={<Target size={36} />}
            title="No opportunities match"
            hint="Adjust the view or clear your search to see the full pipeline."
          />
        </SectionCard>
      ) : viewMode === 'cards' ? (
        <div className="pipe-board">
          {columns.map((col) => (
            <div key={col.stage} className="pipe-col">
              <div className="pipe-col-head">
                <span className="pipe-col-title">
                  <span className="pipe-col-dot" style={{ background: STAGE_DOT[col.stage] }} />
                  <span className="pipe-col-name">{col.stage}</span>
                </span>
                <span className="pipe-col-count">{col.items.length}</span>
              </div>
              {col.sum > 0 && <div className="pipe-col-sum">{money(col.sum, { compact: true })}</div>}
              <div className="pipe-col-body">
                {col.items.length === 0 ? (
                  <div className="pipe-col-empty">No deals</div>
                ) : (
                  col.items.map((o) => <PipeCard key={o.id} opp={o} data={data} navigate={navigate} />)
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <SectionCard
          title={<>Pipeline <b>Detail</b></>}
          eyebrow="Table view"
          right={<span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{filtered.length} deals</span>}
          pad={false}
        >
          <PipelineTable opps={filtered} data={data} onRow={(o) => navigate(`/opportunities/${o.id}`)} />
        </SectionCard>
      )}
    </>
  )
}

/* ---------- Kanban card ---------- */
function PipeCard({
  opp, data, navigate,
}: {
  opp: Opportunity
  data: Dataset
  navigate: (to: string) => void
}) {
  const acct = accountById(data, opp.principalAccountId)
  const sourceContact = contactById(data, opp.sourceContactId)
  const closeLabel = opp.estCloseQuarter ?? (opp.estCloseDate ? date(opp.estCloseDate, 'short') : null)
  const stopLink = (e: MouseEvent) => e.stopPropagation()
  return (
    <div
      className="pipe-card card--clickable"
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/opportunities/${opp.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/opportunities/${opp.id}`)
        }
      }}
    >
      <div className="pipe-card-top">
        <Link to={`/opportunities/${opp.id}`} className="pipe-card-name" onClick={stopLink}>{opp.name}</Link>
        <Badge tone={TYPE_TONE[opp.type]} variant="soft">{opp.type}</Badge>
      </div>
      {acct && (
        <div className="pipe-card-acct">
          <Link to={`/accounts/${acct.id}`} onClick={stopLink}>{acct.name}</Link>
        </div>
      )}
      {sourceContact && opp.sourceReferredBy && (
        <div className="pipe-card-source">
          <GitBranch size={12} />
          <span>Sourced by <Link to={`/contacts/${sourceContact.id}`} onClick={stopLink}>{contactName(sourceContact)}</Link></span>
        </div>
      )}
      {opp.competitivePosition && (
        <div className="pipe-card-tags">
          <Badge tone={POSITION_TONE[opp.competitivePosition]} variant="outline">{opp.competitivePosition}</Badge>
        </div>
      )}
      <div className="pipe-card-foot">
        <span className="pipe-card-size">{opp.dealSize != null ? money(opp.dealSize, { compact: true }) : '—'}</span>
        {closeLabel && <span className="pipe-card-close">{closeLabel}</span>}
      </div>
    </div>
  )
}

/* ---------- Detail table ---------- */
function PipelineTable({
  opps, data, onRow,
}: {
  opps: Opportunity[]
  data: Dataset
  onRow: (o: Opportunity) => void
}) {
  const cols: Column<Opportunity>[] = [
    {
      key: 'name',
      header: 'Deal',
      sortValue: (o) => o.name,
      render: (o) => (
        <Link to={`/opportunities/${o.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
          {o.name}
        </Link>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortValue: (o) => o.type,
      render: (o) => <Badge tone={TYPE_TONE[o.type]} variant="soft">{o.type}</Badge>,
    },
    {
      key: 'account',
      header: 'Principal Account',
      sortValue: (o) => accountById(data, o.principalAccountId)?.name ?? '',
      render: (o) => {
        const a = accountById(data, o.principalAccountId)
        return a ? <Link to={`/accounts/${a.id}`} onClick={(e) => e.stopPropagation()}>{a.name}</Link> : '—'
      },
    },
    {
      key: 'stage',
      header: 'Stage',
      sortValue: (o) => STAGE_ORDER.indexOf(stageBucket(o)),
      render: (o) => {
        const won = /won/i.test(o.salesStage)
        return <Badge tone={won ? 'ok' : 'neutral'} variant="soft" dot={won}>{o.salesStage}</Badge>
      },
    },
    {
      key: 'salesType',
      header: 'Sales Type',
      sortValue: (o) => o.salesType,
      render: (o) => <span className="muted">{o.salesType}</span>,
    },
    {
      key: 'position',
      header: 'Position',
      sortValue: (o) => o.competitivePosition ?? '',
      render: (o) =>
        o.competitivePosition
          ? <Badge tone={POSITION_TONE[o.competitivePosition]} variant="outline">{o.competitivePosition}</Badge>
          : <span className="dim">—</span>,
    },
    {
      key: 'size',
      header: 'Deal Size',
      align: 'right',
      sortValue: (o) => o.dealSize ?? 0,
      render: (o) => <span className="num">{o.dealSize != null ? money(o.dealSize, { compact: true }) : '—'}</span>,
    },
    {
      key: 'close',
      header: 'Est. Close',
      align: 'right',
      sortValue: (o) => o.estCloseQuarter ?? o.estCloseDate ?? '',
      render: (o) => (
        <span className="muted">{o.estCloseQuarter ?? (o.estCloseDate ? date(o.estCloseDate, 'short') : '—')}</span>
      ),
    },
  ]

  return (
    <DataTable<Opportunity>
      columns={cols}
      rows={opps}
      getRowKey={(o) => o.id}
      onRowClick={onRow}
      initialSort={{ key: 'size', dir: 'desc' }}
      emptyText="No deals."
    />
  )
}
